import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { lookupUkPostcode } from '../../utils/ukPostcode';
import {
  DEFAULT_PROPERTY_FORM, LOCATION_TAGS, LEGAL_OWNER_TITLES, PropertyFormState,
  propertyToForm, serializePropertyForm, expiryHint, autoPricePerSqm, formatLegalOwner,
} from './propertyFormShared';
import PropertyPhotosBlock, { PropertyPhoto } from './PropertyPhotosBlock';
import { PROPERTY_PAGE_CSS } from './propertyPageStyles';

const CSS = `
.pf-page { min-height: 100vh; background: #fafafa; font-family: 'DM Sans', system-ui, sans-serif; padding-bottom: 88px; }
.pf-top { position: sticky; top: 0; z-index: 20; background: #fff; border-bottom: 1px solid #e5e7eb; padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.pf-back { background: none; border: none; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; padding: 0; display: flex; align-items: center; gap: 6px; }
.pf-back:hover { color: #111; }
.pf-title { margin: 0; font-size: 22px; font-weight: 800; color: #111; letter-spacing: -0.02em; }
.pf-sub { margin: 4px 0 0; font-size: 12px; color: #6b7280; }
.pf-body { max-width: 920px; margin: 0 auto; padding: 24px; }
.pf-section { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 20px 22px; margin-bottom: 16px; }
.pf-section-title { font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #374151; margin: 0 0 16px; padding-bottom: 10px; border-bottom: 1px solid #f3f4f6; }
.pf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
.pf-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.pf-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.pf-span2 { grid-column: span 2; }
.pf-span-all { grid-column: 1 / -1; }
.pf-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.pf-req { color: #16a34a; }
.pf-input, .pf-select, .pf-textarea { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; color: #111; background: #fff; box-sizing: border-box; font-family: inherit; }
.pf-input:focus, .pf-select:focus, .pf-textarea:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 2px rgba(22,163,74,0.12); }
.pf-hint { font-size: 11px; color: #6b7280; margin-top: 4px; }
.pf-hint-warn { color: #d97706; font-weight: 600; }
.pf-hint-danger { color: #dc2626; font-weight: 600; }
.pf-hint-ok { color: #16a34a; font-weight: 600; }
.pf-find { padding: 10px 16px; border: none; border-radius: 8px; background: #111; color: #fff; font-weight: 700; font-size: 13px; cursor: pointer; white-space: nowrap; }
.pf-find:hover { background: #16a34a; }
.pf-find:disabled { opacity: 0.5; cursor: default; }
.pf-tag { padding: 6px 14px; border-radius: 20px; border: 1px solid #d1d5db; background: #fff; color: #6b7280; font-size: 13px; font-weight: 500; cursor: pointer; }
.pf-tag.on { border-color: #16a34a; background: #f0fdf4; color: #166534; font-weight: 700; }
.pf-tags-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.pf-custom-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.pf-custom-tag { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: #f3f4f6; border-radius: 16px; font-size: 12px; font-weight: 600; color: #374151; }
.pf-custom-tag button { background: none; border: none; cursor: pointer; color: #9ca3af; font-size: 14px; line-height: 1; padding: 0; }
.pf-check-row { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; margin-top: 4px; }
.pf-check-row input { width: 18px; height: 18px; accent-color: #16a34a; margin-top: 2px; flex-shrink: 0; }
.pf-check-label { font-size: 13px; font-weight: 600; color: #111; cursor: pointer; }
.pf-check-sub { font-size: 12px; color: #6b7280; font-weight: 400; margin-top: 2px; }
.pf-footer { position: fixed; bottom: 0; left: 0; right: 0; z-index: 30; background: #fff; border-top: 1px solid #e5e7eb; padding: 14px 24px; display: flex; justify-content: flex-end; gap: 10px; box-shadow: 0 -4px 20px rgba(0,0,0,0.06); }
.pf-btn-cancel { padding: 11px 22px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #374151; font-weight: 700; font-size: 14px; cursor: pointer; }
.pf-btn-save { padding: 11px 28px; border: none; border-radius: 8px; background: #16a34a; color: #fff; font-weight: 800; font-size: 14px; cursor: pointer; }
.pf-btn-save:disabled { opacity: 0.6; cursor: default; }
.pf-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 13px; font-weight: 600; }
.pf-loading { text-align: center; padding: 80px 24px; color: #9ca3af; }
@media (max-width: 640px) {
  .pf-grid-4 { grid-template-columns: 1fr 1fr; }
  .pf-grid-2 { grid-template-columns: 1fr; }
  .pf-span2 { grid-column: span 1; }
}
`;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="pf-section">
      <h2 className="pf-section-title">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, required, hint, hintClass, children }: {
  label: string; required?: boolean; hint?: string; hintClass?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="pf-label">{label}{required && <span className="pf-req"> *</span>}</label>
      {children}
      {hint && <div className={`pf-hint ${hintClass || ''}`}>{hint}</div>}
    </div>
  );
}

export default function PropertyFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { token } = useAuth();

  const [form, setForm] = useState<PropertyFormState>({ ...DEFAULT_PROPERTY_FORM });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [customTagInput, setCustomTagInput] = useState('');
  const [postcodeLookup, setPostcodeLookup] = useState<{ addresses: string[]; district?: string; loading: boolean; error: string }>({
    addresses: [], loading: false, error: '',
  });
  const [photos, setPhotos] = useState<PropertyPhoto[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<{ id: string; url: string; file: File }[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [rooms, setRooms] = useState<any[]>([]);
  const [roomForm, setRoomForm] = useState({ room_name: '', room_number: '', size_sqm: '', size_sqft: '', size_description: 'medium' });
  const [addingRoom, setAddingRoom] = useState(false);
  const [savingRoom, setSavingRoom] = useState(false);
  const saveInFlight = useRef(false);

  const set = <K extends keyof PropertyFormState>(key: K, val: PropertyFormState[K]) =>
    setForm(p => ({ ...p, [key]: val }));

  const loadProperty = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/properties/${id}/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Property not found');
      const d = await r.json();
      setForm(propertyToForm(d.property));
      setPhotos(d.photos || []);
      setRooms(d.rooms || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load property');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { if (isEdit) loadProperty(); }, [isEdit, loadProperty]);

  const findAddresses = async () => {
    if (!form.postcode.trim() || !token) return;
    setPostcodeLookup(p => ({ ...p, loading: true, error: '' }));
    try {
      const r = await lookupUkPostcode(form.postcode, token);
      setPostcodeLookup({ addresses: r.addresses || [], district: r.district, loading: false, error: '' });
      if (r.postcode) set('postcode', r.postcode);
    } catch (e: any) {
      setPostcodeLookup({ addresses: [], loading: false, error: e.message || 'Lookup failed' });
    }
  };

  const toggleTag = (tag: string) => {
    setForm(p => {
      const has = p.tags.includes(tag);
      const tags = has ? p.tags.filter(t => t !== tag) : [...p.tags, tag];
      const updates: Partial<PropertyFormState> = { tags };
      if (tag === 'Near transport') updates.near_transport = has ? 'no' : 'yes';
      if (tag === 'Near shops') updates.near_shops = has ? 'no' : 'yes';
      return { ...p, ...updates, tags };
    });
  };

  const addCustomTag = () => {
    const t = customTagInput.trim();
    if (!t || form.tags.includes(t)) { setCustomTagInput(''); return; }
    setForm(p => ({ ...p, tags: [...p.tags, t] }));
    setCustomTagInput('');
  };

  const onRentOrAreaChange = (key: 'monthly_rent' | 'total_area_sqm', val: string) => {
    setForm(p => {
      const next = { ...p, [key]: val };
      const auto = autoPricePerSqm(
        key === 'monthly_rent' ? val : p.monthly_rent,
        key === 'total_area_sqm' ? val : p.total_area_sqm,
      );
      if (auto && !p.price_per_sqm) next.price_per_sqm = auto;
      return next;
    });
  };

  const stagePhotos = (files: FileList) => {
    const room = 5 - photos.length - pendingPhotos.length;
    if (room <= 0) return;
    const next = Array.from(files).slice(0, room).map(f => ({
      id: `${Date.now()}-${Math.random()}`, url: URL.createObjectURL(f), file: f,
    }));
    setPendingPhotos(p => [...p, ...next]);
  };

  const removePendingPhoto = (pid: string) => {
    setPendingPhotos(p => {
      const item = p.find(x => x.id === pid);
      if (item) URL.revokeObjectURL(item.url);
      return p.filter(x => x.id !== pid);
    });
  };

  const uploadPhotos = async (files: FileList) => {
    if (!token || !id || !files.length) return;
    setUploadingPhotos(true);
    const fd = new FormData();
    Array.from(files).slice(0, 5 - photos.length).forEach(f => fd.append('photos', f));
    try {
      await fetch(`${API_URL}/landlord/properties/${id}/photos`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const r = await fetch(`${API_URL}/landlord/properties/${id}/summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setPhotos((await r.json()).photos || []);
    } finally { setUploadingPhotos(false); }
  };

  const deletePhoto = async (photoId: number) => {
    if (!token || !id) return;
    await fetch(`${API_URL}/landlord/properties/${id}/photos/${photoId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    setPhotos(p => p.filter(ph => ph.id !== photoId));
  };

  const uploadPendingToProperty = async (propId: string | number): Promise<boolean> => {
    if (!token || !pendingPhotos.length) return true;
    const fd = new FormData();
    pendingPhotos.forEach(p => fd.append('photos', p.file));
    const r = await fetch(`${API_URL}/landlord/properties/${propId}/photos`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    if (!r.ok) return false;
    pendingPhotos.forEach(p => URL.revokeObjectURL(p.url));
    setPendingPhotos([]);
    return true;
  };

  const save = async () => {
    if (saveInFlight.current || saving) return;
    if (!form.address.trim() || !form.postcode.trim() || !form.monthly_rent) {
      setError('Address, postcode and monthly rent are required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    saveInFlight.current = true;
    setSaving(true); setError('');
    try {
      const body = serializePropertyForm(form);
      const url = isEdit
        ? `${API_URL}/landlord/properties/${id}`
        : `${API_URL}/landlord/properties`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.error || 'Failed to save');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        saveInFlight.current = false;
        setSaving(false);
        return;
      }
      const propId = isEdit ? id : (d.property?.id ?? d.id);
      if (propId && pendingPhotos.length) {
        const photosOk = await uploadPendingToProperty(propId);
        if (!photosOk) {
          setError('Property saved but photos failed to upload — add them in Edit.');
          saveInFlight.current = false;
          setSaving(false);
          if (isEdit) navigate(`/landlord/properties/${propId}/edit`);
          else navigate('/landlord/properties');
          return;
        }
      }
      if (isEdit && propId) navigate(`/landlord/properties/${propId}`);
      else navigate('/landlord/properties');
    } catch {
      setError('Failed to save property');
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const addRoom = async () => {
    if (!token || !id || !roomForm.room_name.trim()) return;
    setSavingRoom(true);
    try {
      await fetch(`${API_URL}/landlord/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          property_id: Number(id), property_address: form.address, ...roomForm,
          size_sqm: roomForm.size_sqm ? Number(roomForm.size_sqm) : null,
          size_sqft: roomForm.size_sqft ? Number(roomForm.size_sqft) : null,
        }),
      });
      setRoomForm({ room_name: '', room_number: '', size_sqm: '', size_sqft: '', size_description: 'medium' });
      setAddingRoom(false);
      const r = await fetch(`${API_URL}/landlord/properties/${id}/summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setRooms((await r.json()).rooms || []);
    } finally { setSavingRoom(false); }
  };

  const deleteRoom = async (roomId: number) => {
    if (!token || !id || !window.confirm('Remove this room?')) return;
    await fetch(`${API_URL}/landlord/rooms/${roomId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setRooms(r => r.filter(rm => rm.id !== roomId));
  };

  const gasHint = expiryHint(form.gas_safety_expiry);
  const eicrHint = expiryHint(form.eicr_expiry);
  const insHint = expiryHint(form.insurance_expiry);

  if (loading) {
    return (
      <div className="pf-page">
        <style>{CSS}{PROPERTY_PAGE_CSS}</style>
        <div className="pf-loading">Loading property…</div>
      </div>
    );
  }

  return (
    <div className="pf-page">
      <style>{CSS}{PROPERTY_PAGE_CSS}</style>

      <header className="pf-top">
        <div>
          <button type="button" className="pf-back" onClick={() => navigate('/landlord/properties')}>
            ← Back to Properties
          </button>
          <h1 className="pf-title">{isEdit ? 'Edit Property' : 'Add New Property'}</h1>
          <p className="pf-sub">
            {isEdit ? form.address || 'Update all property details' : 'Enter full details — all fields are saved for compliance and lettings'}
          </p>
        </div>
      </header>

      <div className="pf-body">
        {error && <div className="pf-error">{error}</div>}

        <Section title="Basic Information">
          <div className="pf-grid">
            <Field label="Postcode" required hint={postcodeLookup.district ? `📍 ${postcodeLookup.district}` : postcodeLookup.error || undefined} hintClass={postcodeLookup.error ? 'pf-hint-danger' : ''}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="pf-input" value={form.postcode} onChange={e => set('postcode', e.target.value.toUpperCase())} placeholder="PO5 2AB" style={{ flex: 1 }} />
                <button type="button" className="pf-find" onClick={findAddresses} disabled={postcodeLookup.loading || !form.postcode.trim()}>
                  {postcodeLookup.loading ? '…' : 'Find'}
                </button>
              </div>
            </Field>
            <div className="pf-span2">
              <Field label="Address" required>
                {postcodeLookup.addresses.length > 0 ? (
                  <select className="pf-select" value={form.address} onChange={e => set('address', e.target.value)}>
                    <option value="">— Select address —</option>
                    {postcodeLookup.addresses.map((a, i) => <option key={i} value={a}>{a}</option>)}
                  </select>
                ) : (
                  <input className="pf-input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="14 Ocean Road, Southsea" />
                )}
              </Field>
            </div>
            <Field label="Property Type">
              <select className="pf-select" value={form.property_type} onChange={e => set('property_type', e.target.value)}>
                {['Terraced House', 'End of Terrace', 'Semi-Detached', 'Detached', 'Flat / Apartment', 'Studio', 'HMO', 'Bungalow', 'Maisonette', 'Shop / Retail', 'Office', 'Industrial'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Unit Type">
              <select className="pf-select" value={form.unit_type} onChange={e => set('unit_type', e.target.value)}>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="shop">Shop</option>
                <option value="parking">Parking</option>
                <option value="storage">Storage</option>
                <option value="garage">Garage</option>
              </select>
            </Field>
            <Field label="Status">
              <select className="pf-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="to_let">To Let</option>
                <option value="ready_to_rent">Ready to Rent</option>
                <option value="available">Available / Vacant</option>
                <option value="under_offer">Let Agreed / Under Offer</option>
                <option value="let">Let (Occupied)</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Financial">
          <div className="pf-grid pf-grid-2">
            <Field label="Monthly Rent (£)" required>
              <input type="number" className="pf-input" value={form.monthly_rent} onChange={e => onRentOrAreaChange('monthly_rent', e.target.value)} placeholder="1200" />
            </Field>
            <Field label="Bills Included">
              <select className="pf-select" value={form.bills_included} onChange={e => set('bills_included', e.target.value)}>
                <option value="no">No</option>
                <option value="yes">Yes — all bills</option>
                <option value="part">Some bills</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Rooms & Size">
          <div className="pf-grid">
            <Field label="Rooms (bedrooms)">
              <input type="number" min="0" className="pf-input" value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)} />
            </Field>
            <Field label="Bathrooms">
              <input type="number" min="1" className="pf-input" value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)} />
            </Field>
            <Field label="Total Area (m²)" hint="Used for yield and price per m²">
              <input type="number" className="pf-input" value={form.total_area_sqm} onChange={e => onRentOrAreaChange('total_area_sqm', e.target.value)} placeholder="e.g. 85" />
            </Field>
            <Field label="Price per m² (£)" hint={form.monthly_rent && form.total_area_sqm ? 'Auto-calculated from rent ÷ area if left blank' : undefined}>
              <input type="number" className="pf-input" value={form.price_per_sqm} onChange={e => set('price_per_sqm', e.target.value)} placeholder="e.g. 14.10" />
            </Field>
            <Field label="Furnished">
              <select className="pf-select" value={form.furnished} onChange={e => set('furnished', e.target.value)}>
                <option value="no">Unfurnished</option>
                <option value="part">Part Furnished</option>
                <option value="yes">Furnished</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Building">
          <div className="pf-grid pf-grid-2" style={{ marginBottom: 14 }}>
            <Field label="Floor">
              <input type="number" min="0" className="pf-input" value={form.floor} onChange={e => set('floor', e.target.value)} placeholder="e.g. 2" />
            </Field>
            <Field label="Total Floors in Building">
              <input type="number" min="1" className="pf-input" value={form.total_floors} onChange={e => set('total_floors', e.target.value)} placeholder="e.g. 3" />
            </Field>
          </div>
          <div className="pf-grid pf-grid-4">
            <Field label="Parking">
              <select className="pf-select" value={form.parking} onChange={e => set('parking', e.target.value)}>
                <option value="no">No</option>
                <option value="yes">Yes — allocated</option>
                <option value="street">Street only</option>
              </select>
            </Field>
            <Field label="Ground Floor">
              <select className="pf-select" value={form.ground_floor} onChange={e => set('ground_floor', e.target.value)}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </Field>
            <Field label="Wheelchair Accessible">
              <select className="pf-select" value={form.wheelchair_accessible} onChange={e => set('wheelchair_accessible', e.target.value)}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </Field>
            <Field label="Has Lift">
              <select className="pf-select" value={form.has_lift} onChange={e => set('has_lift', e.target.value)}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Legal Landlord (for AST & contracts)">
          <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
            The person or company named on tenancy agreements — use the legal title (Mr, Mrs, Lord, Lady, etc.) and full name as it should appear on documents.
          </p>
          <div className="pf-grid">
            <Field label="Title">
              <select className="pf-select" value={form.legal_owner_title} onChange={e => set('legal_owner_title', e.target.value)}>
                <option value="">— Select —</option>
                {LEGAL_OWNER_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Legal name" hint={formatLegalOwner(form.legal_owner_title, form.legal_owner_name) ? `On AST: ${formatLegalOwner(form.legal_owner_title, form.legal_owner_name)}` : 'Required for compliant tenancy paperwork'}>
              <input className="pf-input" value={form.legal_owner_name} onChange={e => set('legal_owner_name', e.target.value)} placeholder="e.g. John Smith or Smith Properties Ltd" />
            </Field>
          </div>
        </Section>

        <Section title="Legal & Compliance">
          <div className="pf-grid">
            <Field label="EPC Rating" hint="Minimum E required to let in England">
              <select className="pf-select" value={form.epc_rating} onChange={e => set('epc_rating', e.target.value)}>
                <option value="">Not specified</option>
                {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Council Tax Band">
              <select className="pf-select" value={form.council_tax_band} onChange={e => set('council_tax_band', e.target.value)}>
                <option value="">Not specified</option>
                {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Tenure">
              <select className="pf-select" value={form.tenure} onChange={e => set('tenure', e.target.value)}>
                <option value="freehold">Freehold</option>
                <option value="leasehold">Leasehold</option>
                <option value="share_of_freehold">Share of Freehold</option>
                <option value="commonhold">Commonhold</option>
              </select>
            </Field>
            {(form.property_type === 'HMO' || form.hmo_license) && (
              <Field label="HMO Licence Number">
                <input className="pf-input" value={form.hmo_license} onChange={e => set('hmo_license', e.target.value)} placeholder="e.g. HMO/2024/00123" />
              </Field>
            )}
            <Field label="Gas Safety Expiry (CP12)" hint={gasHint?.text} hintClass={gasHint ? (gasHint.color === '#dc2626' ? 'pf-hint-danger' : gasHint.color === '#d97706' ? 'pf-hint-warn' : gasHint.color === '#16a34a' ? 'pf-hint-ok' : '') : ''}>
              <input type="date" className="pf-input" value={form.gas_safety_expiry} onChange={e => set('gas_safety_expiry', e.target.value)} />
            </Field>
            <Field label="EICR Expiry (Electrical)" hint={eicrHint?.text} hintClass={eicrHint ? (eicrHint.color === '#dc2626' ? 'pf-hint-danger' : eicrHint.color === '#d97706' ? 'pf-hint-warn' : eicrHint.color === '#16a34a' ? 'pf-hint-ok' : '') : ''}>
              <input type="date" className="pf-input" value={form.eicr_expiry} onChange={e => set('eicr_expiry', e.target.value)} />
            </Field>
            <Field label="Landlord Insurance Expiry" hint={insHint?.text} hintClass={insHint ? (insHint.color === '#dc2626' ? 'pf-hint-danger' : insHint.color === '#d97706' ? 'pf-hint-warn' : insHint.color === '#16a34a' ? 'pf-hint-ok' : '') : ''}>
              <input type="date" className="pf-input" value={form.insurance_expiry} onChange={e => set('insurance_expiry', e.target.value)} />
            </Field>
          </div>
          <div className="pf-check-row">
            <input type="checkbox" id="ready-rent" checked={form.ready_to_rent}
              onChange={e => setForm(p => ({ ...p, ready_to_rent: e.target.checked, status: e.target.checked ? 'ready_to_rent' : (p.status === 'ready_to_rent' ? 'to_let' : p.status) }))} />
            <label htmlFor="ready-rent" className="pf-check-label">
              Mark as Ready to Rent
              <div className="pf-check-sub">Property is compliant, vacant, and ready to market. Shows in Ready to Rent filter on your portfolio.</div>
            </label>
          </div>
        </Section>

        <Section title="Location Tags">
          <div className="pf-tags-row">
            {LOCATION_TAGS.map(tag => (
              <button key={tag} type="button" className={`pf-tag${form.tags.includes(tag) ? ' on' : ''}`} onClick={() => toggleTag(tag)}>
                {form.tags.includes(tag) ? '✓ ' : ''}{tag}
              </button>
            ))}
          </div>
          <Field label="Custom tags" hint="Press Enter to add each tag (e.g. Near hospital, Student area)">
            <input className="pf-input" value={customTagInput} onChange={e => setCustomTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
              placeholder="Type a tag and press Enter" />
          </Field>
          {form.tags.filter(t => !LOCATION_TAGS.includes(t as typeof LOCATION_TAGS[number])).length > 0 && (
            <div className="pf-custom-tags">
              {form.tags.filter(t => !LOCATION_TAGS.includes(t as typeof LOCATION_TAGS[number])).map(tag => (
                <span key={tag} className="pf-custom-tag">
                  {tag}
                  <button type="button" onClick={() => setForm(p => ({ ...p, tags: p.tags.filter(t => t !== tag) }))}>×</button>
                </span>
              ))}
            </div>
          )}
        </Section>

        {isEdit && (
          <Section title={`Rooms (${rooms.length})`}>
            {rooms.length === 0 && !addingRoom && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>No rooms defined — add rooms for HMO or multi-let properties.</p>}
            {rooms.map(rm => (
              <div key={rm.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#f9fafb', borderRadius: 8, marginBottom: 6 }}>
                <span style={{ flex: 1, fontWeight: 700, fontSize: 13 }}>{rm.room_name}{rm.room_number ? ` · ${rm.room_number}` : ''}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{[rm.size_sqm && `${rm.size_sqm}m²`, rm.size_description].filter(Boolean).join(' · ')}</span>
                <button type="button" onClick={() => deleteRoom(rm.id)} style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
              </div>
            ))}
            {addingRoom ? (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginTop: 8 }}>
                <div className="pf-grid pf-grid-2">
                  <Field label="Room Name" required><input className="pf-input" value={roomForm.room_name} onChange={e => setRoomForm(p => ({ ...p, room_name: e.target.value }))} placeholder="Master Room" /></Field>
                  <Field label="Room Number"><input className="pf-input" value={roomForm.room_number} onChange={e => setRoomForm(p => ({ ...p, room_number: e.target.value }))} placeholder="Room 1" /></Field>
                  <Field label="Size (m²)"><input type="number" className="pf-input" value={roomForm.size_sqm} onChange={e => setRoomForm(p => ({ ...p, size_sqm: e.target.value }))} /></Field>
                  <Field label="Size description">
                    <select className="pf-select" value={roomForm.size_description} onChange={e => setRoomForm(p => ({ ...p, size_description: e.target.value }))}>
                      {['single', 'double', 'medium', 'large', 'small'].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </Field>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button type="button" className="pf-find" onClick={addRoom} disabled={savingRoom}>{savingRoom ? 'Saving…' : 'Add Room'}</button>
                  <button type="button" className="pf-btn-cancel" style={{ padding: '10px 16px' }} onClick={() => setAddingRoom(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setAddingRoom(true)} style={{ marginTop: 8, width: '100%', padding: '9px 16px', border: '1.5px dashed #d1d5db', borderRadius: 8, background: '#fafafa', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>+ Add Room</button>
            )}
          </Section>
        )}

        <Section title="Property Photos">
          <PropertyPhotosBlock
            photos={photos}
            pendingPreviews={pendingPhotos}
            uploading={uploadingPhotos}
            onStageFiles={isEdit ? undefined : stagePhotos}
            onUpload={isEdit ? uploadPhotos : undefined}
            onDelete={isEdit ? deletePhoto : undefined}
            onRemovePending={isEdit ? undefined : removePendingPhoto}
            hint={isEdit ? 'Add up to 5 photos. Saved immediately when uploaded.' : 'Select photos now — they upload automatically when you save the property.'}
          />
        </Section>

        <Section title="Description & Notes">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Property Description" hint="Used for listings, tenant matching, and viewings">
              <textarea className="pf-textarea" rows={4} value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe the property — layout, condition, neighbourhood, who it suits…" />
            </Field>
            <Field label="Shared Spaces & Access Notes" hint="Keys, codes, shared gardens, bin stores, parking arrangements">
              <textarea className="pf-textarea" rows={3} value={form.shared_spaces} onChange={e => set('shared_spaces', e.target.value)}
                placeholder="Shared spaces, access instructions, key codes…" />
            </Field>
          </div>
        </Section>
      </div>

      <footer className="pf-footer">
        <button type="button" className="pf-btn-cancel" onClick={() => isEdit && id ? navigate(`/landlord/properties/${id}`) : navigate('/landlord/properties')}>
          {isEdit ? 'Cancel' : '← Back'}
        </button>
        <button type="button" className="pf-btn-save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Property'}
        </button>
      </footer>
    </div>
  );
}
